import {
  AbstractMesh,
  BaseTexture,
  Color3,
  Engine,
  Material,
  MaterialDefines,
  MaterialPluginBase,
  type Nullable,
  PBRMaterial,
  RegisterMaterialPlugin,
  Scene,
  SubMesh,
  Texture,
  UniformBuffer,
  UnregisterMaterialPlugin,
} from '@babylonjs/core';
export class DanjiDecalDefines extends MaterialDefines {
  DANJIDECAL = false;
  DANJIDECALEDGE = false;
  DANJIDECALPATTARN = false;
}

export class DanjiDecalConfiguration {}

export class DanjiDecalMaterialPlugin extends MaterialPluginBase {
  //------------------------------------------------------------------------------------
  // #### 등록, 제거 ####
  //------------------------------------------------------------------------------------
  public static materialName = 'DanjiDecal';

  public static register() {
    RegisterMaterialPlugin(`${DanjiDecalMaterialPlugin.materialName}Receiver`, (material) => {
      if (material instanceof PBRMaterial) {
        material.transparencyMode = 1;
        material.alphaCutOff = 0.5;
      }

      // @ts-ignore
      material.danjiDecal = new DanjiDecalMaterialPlugin(material);
      // @ts-ignore
      return material.danjiDecal;
    });
  }
  public static unregister() {
    UnregisterMaterialPlugin(`${DanjiDecalMaterialPlugin.materialName}Receiver`);
  }

  //------------------------------------------------------------------------------------
  // #### 변수 정의 ####
  //------------------------------------------------------------------------------------
  private _config: DanjiDecalConfiguration | undefined;
  get config() {
    return this._config;
  }
  set config(value) {
    if (this._config === value) {
      return;
    }
    this._config = value;
  }

  private _isEnabled = false;
  get isEnabled() {
    return this._isEnabled;
  }
  set isEnabled(enabled) {
    if (this._isEnabled === enabled) {
      return;
    }
    this._isEnabled = enabled;
    this.markAllDefinesAsDirty();
    this._enable(this._isEnabled);
  }

  private _maskTexture: Nullable<BaseTexture> = null;
  public get maskTexture(): Nullable<BaseTexture> {
    return this._maskTexture;
  }
  public set maskTexture(t: Nullable<BaseTexture>) {
    if (this._maskTexture === t) return;
    this._maskTexture = t;
    this._material.markAsDirty(Material.TextureDirtyFlag);
    this.markAllDefinesAsDirty();
  }
  private get isMaskTextureReady() {
    return !!this._maskTexture && this._maskTexture.isReadyOrNotBlocking();
  }

  private _patternTexture: Nullable<Texture> = null;
  public get patternTexture(): Nullable<Texture> {
    return this._patternTexture;
  }
  public set patternTexture(t: Nullable<Texture>) {
    if (this._patternTexture === t) return;
    this._patternTexture = t;
    this._material.markAsDirty(Material.TextureDirtyFlag);
    this.markAllDefinesAsDirty();
  }
  private get isPatternTextureReady() {
    return !!this._patternTexture && this._patternTexture.isReadyOrNotBlocking();
  }

  private _patternColor: Color3 = Color3.Black();
  public set patternColor(value: Color3) {
    if (this._patternColor === value) return;
    this._patternColor = value;
    this._material.markAsDirty(Material.MiscDirtyFlag);
  }

  private _maskEdge: boolean = false;
  public set maskEdge(value: boolean) {
    if (this._maskEdge === value) return;
    this._maskEdge = value;
    this._material.markAsDirty(Material.MiscDirtyFlag);
  }

  private _maskLayer1: number = 0;
  public set maskLayer1(value: number) {
    if (this._maskLayer1 === value) return;
    this._maskLayer1 = value;
    this._material.markAsDirty(Material.MiscDirtyFlag);
  }

  private _maskLayer2: number = 0;
  public set maskLayer2(value: number) {
    if (this._maskLayer2 === value) return;
    this._maskLayer2 = value;
    this._material.markAsDirty(Material.MiscDirtyFlag);
  }

  private _maskLayer3: number = 0;
  public set maskLayer3(value: number) {
    if (this._maskLayer3 === value) return;
    this._maskLayer3 = value;
    this._material.markAsDirty(Material.MiscDirtyFlag);
  }

  private _maskLayer4: number = 0;
  public set maskLayer4(value: number) {
    if (this._maskLayer4 === value) return;
    this._maskLayer4 = value;
    this._material.markAsDirty(Material.MiscDirtyFlag);
  }

  //------------------------------------------------------------------------------------
  // #### 생성자 ####
  //------------------------------------------------------------------------------------
  constructor(material: PBRMaterial, addToPluginList?: boolean) {
    super(material, DanjiDecalMaterialPlugin.materialName, 100, new DanjiDecalDefines(), addToPluginList);
  }
  public override getClassName() {
    return `${DanjiDecalMaterialPlugin.materialName}Material`;
  }

  public override dispose(forceDisposeTextures?: boolean | undefined) {
    if (forceDisposeTextures) {
      this._maskTexture?.dispose();
    }
  }

  //------------------------------------------------------------------------------------
  // #### 사전 정의 ####
  //------------------------------------------------------------------------------------
  public override isReadyForSubMesh(
    defines: DanjiDecalDefines,
    scene: Scene,
    engine: Engine,
    subMesh: SubMesh
  ): boolean {
    if (!this._isEnabled) {
      return true;
    }

    if (defines._areTexturesDirty) {
      if (scene.texturesEnabled) {
        if (!this.isMaskTextureReady) {
          return false;
        }

        if (defines.DANJIDECALPATTARN) {
          if (!this.isPatternTextureReady) {
            return false;
          }
        }
      }
    }

    return true;
  }

  public override bindForSubMesh(uniformBuffer: UniformBuffer, scene: Scene, engine: Engine, subMesh: SubMesh): void {
    if (!this._isEnabled) {
      return;
    }

    const defines = subMesh!.materialDefines as unknown as DanjiDecalDefines;
    if (!uniformBuffer.useUbo || !this._material.isFrozen || !uniformBuffer.isSync) {
      if (defines.DANJIDECAL) {
        uniformBuffer.setTexture('maskSampler', this._maskTexture);
        uniformBuffer.updateFloat('maskLayer1', this._maskLayer1);
        uniformBuffer.updateFloat('maskLayer2', this._maskLayer2);
        uniformBuffer.updateFloat('maskLayer3', this._maskLayer3);
        uniformBuffer.updateFloat('maskLayer4', this._maskLayer4);
        if (defines.DANJIDECALPATTARN && this._patternTexture) {
          uniformBuffer.updateColor3('patternColor', this._patternColor);
          uniformBuffer.setTexture('pattarnSampler', this._patternTexture);
          uniformBuffer.updateFloat2('pattarnMapScale', this._patternTexture.uScale, this._patternTexture.vScale);
        }
      }
    }
  }

  public override prepareDefinesBeforeAttributes(defines: DanjiDecalDefines, scene: Scene, mesh: AbstractMesh): void {
    defines.DANJIDECAL = this._isEnabled;
    defines.DANJIDECALEDGE = defines.DANJIDECAL && this._maskEdge;
    defines.DANJIDECALPATTARN = defines.DANJIDECAL && this.isPatternTextureReady;
  }

  //------------------------------------------------------------------------------------
  // #### Custom Value 정의 ####
  //------------------------------------------------------------------------------------
  public override getSamplers(samplers: string[]): void {
    samplers.push('maskSampler');
    samplers.push('pattarnSampler');
  }
  public override getAttributes(attributes: string[], scene: Scene, mesh: AbstractMesh): void {
    attributes.push('maskLayer1');
    attributes.push('maskLayer2');
    attributes.push('maskLayer3');
    attributes.push('maskLayer4');
    attributes.push('patternColor');
    attributes.push('pattarnMapScale');
  }
  public override getUniforms(): {
    ubo?: Array<{ name: string; size?: number; type?: string; arraySize?: number }>;
    vertex?: string;
    fragment?: string;
  } {
    return {
      ubo: [
        { name: 'maskLayer1', size: 1, type: 'float' },
        { name: 'maskLayer2', size: 1, type: 'float' },
        { name: 'maskLayer3', size: 1, type: 'float' },
        { name: 'maskLayer4', size: 1, type: 'float' },
        { name: 'patternColor', size: 3, type: 'vec3' },
        { name: 'pattarnMapScale', size: 2, type: 'vec2' },
      ],
      vertex: `
      #ifdef DANJIDECAL
        #ifdef DANJIDECALPATTARN
          uniform vec2 pattarnMapScale;
        #endif
      #endif
      `,
      fragment: `
          #ifdef DANJIDECAL
            uniform float maskLayer1;
            uniform float maskLayer2;
            uniform float maskLayer3;
            uniform float maskLayer4;
            #ifdef DANJIDECALPATTARN
              uniform vec3 patternColor;
            #endif
          #endif
          
        `,
    };
  }

  //------------------------------------------------------------------------------------
  // #### Custom Code 작성 ####
  //------------------------------------------------------------------------------------
  public override getCustomCode(shaderType: string): Nullable<{ [pointName: string]: string }> {
    return shaderType === 'vertex' ? this.getVertexCode() : this.getFragmentCode();
  }
  public getVertexCode(): Nullable<{ [pointName: string]: string }> {
    return {
      CUSTOM_VERTEX_BEGIN: `
          #ifdef UV1
            #ifdef DANJIDECAL
              varying vec2 vMaskMapUV;
              #ifdef DANJIDECALPATTARN
                varying vec2 vPattarnMapUV;
              #endif
            #endif
          #endif
        `,
      CUSTOM_VERTEX_MAIN_BEGIN: `
          #ifdef UV1
            #ifdef DANJIDECAL
                vMaskMapUV = uv;
                #ifdef DANJIDECALPATTARN
                  vPattarnMapUV = uv * pattarnMapScale;
                #endif
            #endif
          #endif
        `,
    };
  }
  public getFragmentCode(): Nullable<{ [pointName: string]: string }> {
    return {
      CUSTOM_FRAGMENT_DEFINITIONS: `
          #ifdef DANJIDECAL
              uniform highp sampler2D maskSampler;

              #ifdef UV1
                varying vec2 vMaskMapUV;
              #endif

              #ifdef DANJIDECALPATTARN
                varying vec2 vPattarnMapUV;
                uniform highp sampler2D pattarnSampler;
              #endif
          #endif
        `,
      CUSTOM_FRAGMENT_MAIN_BEGIN: `
          vec4 albedoColor = vAlbedoColor;
          #ifdef DANJIDECAL
              #ifdef UV1
                vec4 maskTexture = texture2D(maskSampler, vMaskMapUV);
              #else
                vec4 maskTexture = vAlbedoColor;
              #endif
              float layer = floor(maskTexture.r * 255.0);
              if(layer == 255.0) discard;

              #ifdef DANJIDECALEDGE
                if(layer == maskLayer1 || layer == maskLayer2 || layer == maskLayer3 || layer == maskLayer4)
                {
                    discard;
                }
              #else 
                if(layer != maskLayer1 && layer != maskLayer2)
                {
                    discard;
                }
              #endif

              #ifdef DANJIDECALPATTARN
                vec4 pattarnTexture = texture2D(pattarnSampler, vPattarnMapUV);
                albedoColor.rgb = mix(vAlbedoColor.rgb, patternColor, pattarnTexture.r);
              #endif
          #endif
        `,
      '!#if defined\\(BUMP\\) \\|\\| !defined\\(NORMAL\\) \\|\\| defined\\(FORCENORMALFORWARD\\) \\|\\| defined\\(SPECULARAA\\) \\|\\| defined\\(CLEARCOAT_BUMP\\) \\|\\| defined\\(ANISOTROPIC\\)\n#extension GL_OES_standard_derivatives : enable\n#endif': `#extension GL_OES_standard_derivatives : enable`,
      '!albedoOpacityBlock\\(\nvAlbedoColor,': `
          albedoOpacityBlock(
            albedoColor,
        `,
    };
  }
}

